<template>
  <b-modal
    :visible="value"
    size="xl"
    title="Détails du Ticket"
    hide-footer
    @hidden="$emit('input', false)"
  >
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner />
      <p class="mt-1 mb-0">
        Chargement...
      </p>
    </div>

    <div v-else-if="ticket">
      <!-- Header -->
      <b-card class="mb-2">
        <b-row>
          <b-col
            cols="12"
            md="8"
          >
            <div class="d-flex align-items-start mb-1">
              <b-avatar
                :variant="`light-${getTypeVariant(ticket.ticket_type)}`"
                rounded
                size="56"
                class="mr-1"
              >
                <feather-icon
                  :icon="getTypeIcon(ticket.ticket_type)"
                  size="28"
                />
              </b-avatar>
              <div>
                <h4 class="mb-50">
                  {{ ticket.title }}
                </h4>
                <p class="text-muted mb-0">
                  <strong>{{ ticket.ticket_id }}</strong> •
                  {{ ticket.ticket_type_display }}
                </p>
              </div>
            </div>
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="text-md-right"
          >
            <b-badge
              :variant="getStatusVariant(ticket.status)"
              pill
              class="mb-50"
            >
              {{ ticket.status_display }}
            </b-badge>
            <br>
            <b-badge
              :variant="`light-${getPriorityVariant(ticket.priority)}`"
              pill
            >
              {{ ticket.priority_display }}
            </b-badge>
          </b-col>
        </b-row>
      </b-card>

      <!-- Description -->
      <b-card class="mb-2">
        <h5 class="mb-1">
          Description
        </h5>
        <p
          class="mb-0"
          style="white-space: pre-wrap;"
        >
          {{ ticket.description }}
        </p>
      </b-card>

      <!-- Admin Response -->
      <b-card
        v-if="ticket.admin_response"
        class="mb-2"
        bg-variant="light"
      >
        <h5 class="mb-1">
          <feather-icon
            icon="MessageSquareIcon"
            class="mr-50"
          />
          Réponse de l'équipe
        </h5>
        <p
          class="mb-0"
          style="white-space: pre-wrap;"
        >
          {{ ticket.admin_response }}
        </p>
      </b-card>

      <!-- Comments -->
      <b-card class="mb-2">
        <h5 class="mb-2">
          <feather-icon
            icon="MessageCircleIcon"
            class="mr-50"
          />
          Commentaires ({{ ticket.comments.length }})
        </h5>

        <div
          v-if="ticket.comments.length === 0"
          class="text-center text-muted py-2"
        >
          Aucun commentaire pour l'instant
        </div>

        <div v-else>
          <div
            v-for="comment in ticket.comments"
            :key="comment.id"
            class="mb-2"
          >
            <div class="d-flex align-items-start">
              <b-avatar
                :variant="comment.author_role === 'supplier' ? 'light-primary' : 'light-success'"
                size="40"
                class="mr-1"
              >
                <feather-icon
                  :icon="comment.author_role === 'supplier' ? 'UserIcon' : 'HeadphonesIcon'"
                  size="20"
                />
              </b-avatar>
              <div class="flex-grow-1">
                <div class="d-flex justify-content-between align-items-center mb-50">
                  <strong>{{ comment.author.username }}</strong>
                  <small class="text-muted">{{ formatDate(comment.created_at) }}</small>
                </div>
                <p
                  class="mb-0"
                  style="white-space: pre-wrap;"
                >
                  {{ comment.comment }}
                </p>
              </div>
            </div>
            <hr v-if="comment !== ticket.comments[ticket.comments.length - 1]">
          </div>
        </div>

        <!-- Add Comment Form -->
        <hr>
        <b-form @submit.prevent="addComment">
          <b-form-group label="Ajouter un commentaire">
            <b-form-textarea
              v-model="newComment"
              placeholder="Votre commentaire..."
              rows="3"
            />
          </b-form-group>
          <div class="d-flex justify-content-end">
            <b-button
              type="submit"
              variant="primary"
              :disabled="!newComment || addingComment"
            >
              <b-spinner
                v-if="addingComment"
                small
                class="mr-50"
              />
              {{ addingComment ? 'Envoi...' : 'Ajouter un commentaire' }}
            </b-button>
          </div>
        </b-form>
      </b-card>

      <!-- Attachments -->
      <b-card v-if="ticket.attachments && ticket.attachments.length > 0">
        <h5 class="mb-2">
          <feather-icon
            icon="PaperclipIcon"
            class="mr-50"
          />
          Pièces jointes ({{ ticket.attachments.length }})
        </h5>
        <b-list-group>
          <b-list-group-item
            v-for="attachment in ticket.attachments"
            :key="attachment.id"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <feather-icon
                icon="FileIcon"
                class="mr-50"
              />
              {{ attachment.file_name }}
              <small class="text-muted ml-1">({{ formatFileSize(attachment.file_size) }})</small>
            </div>
            <b-button
              size="sm"
              variant="outline-primary"
              :href="attachment.file_url"
              target="_blank"
            >
              <feather-icon
                icon="DownloadIcon"
                size="16"
              />
            </b-button>
          </b-list-group-item>
        </b-list-group>
      </b-card>

      <!-- Metadata -->
      <b-card bg-variant="light">
        <b-row>
          <b-col
            cols="6"
            md="3"
          >
            <small class="text-muted">Créé le</small>
            <p class="mb-0">
              <strong>{{ formatDate(ticket.created_at) }}</strong>
            </p>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <small class="text-muted">Mis à jour</small>
            <p class="mb-0">
              <strong>{{ formatDate(ticket.updated_at) }}</strong>
            </p>
          </b-col>
          <b-col
            v-if="ticket.resolved_at"
            cols="6"
            md="3"
          >
            <small class="text-muted">Résolu le</small>
            <p class="mb-0">
              <strong>{{ formatDate(ticket.resolved_at) }}</strong>
            </p>
          </b-col>
          <b-col
            v-if="ticket.closed_at"
            cols="6"
            md="3"
          >
            <small class="text-muted">Fermé le</small>
            <p class="mb-0">
              <strong>{{ formatDate(ticket.closed_at) }}</strong>
            </p>
          </b-col>
        </b-row>
      </b-card>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BCard, BRow, BCol, BAvatar, BBadge, BForm, BFormGroup,
  BFormTextarea, BButton, BSpinner, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import axios from '@/libs/axios'
import { formatDate } from '@core/utils/filter'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'TicketDetailModal',
  components: {
    BModal,
    BCard,
    BRow,
    BCol,
    BAvatar,
    BBadge,
    BForm,
    BFormGroup,
    BFormTextarea,
    BButton,
    BSpinner,
    BListGroup,
    BListGroupItem,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    ticketId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      ticket: null,
      loading: false,
      newComment: '',
      addingComment: false,
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.fetchTicketDetails()
      }
    },
  },
  methods: {
    async fetchTicketDetails() {
      this.loading = true
      try {
        const response = await axios.get(`/api/support/supplier-tickets/${this.ticketId}/`)
        this.ticket = response.data
      } catch (error) {
        console.error('Error fetching ticket details:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            text: 'Erreur lors du chargement des détails du ticket',
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
      } finally {
        this.loading = false
      }
    },
    async addComment() {
      if (!this.newComment.trim()) return

      this.addingComment = true
      try {
        await axios.post(`/api/support/supplier-tickets/${this.ticketId}/add-comment/`, {
          comment: this.newComment,
        })
        this.newComment = ''
        await this.fetchTicketDetails()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Succès',
            text: 'Commentaire ajouté avec succès',
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
        this.$emit('ticket-updated')
      } catch (error) {
        console.error('Error adding comment:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erreur',
            text: 'Erreur lors de l\'ajout du commentaire',
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
      } finally {
        this.addingComment = false
      }
    },
    formatDate(date) {
      return formatDate(date, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric',
      })
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    },
    getTypeIcon(type) {
      const icons = {
        bug: 'AlertOctagonIcon',
        feature: 'LightbulbIcon',
        technical: 'MessageCircleIcon',
      }
      return icons[type] || 'HelpCircleIcon'
    },
    getTypeVariant(type) {
      const variants = {
        bug: 'danger',
        feature: 'primary',
        technical: 'success',
      }
      return variants[type] || 'secondary'
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        resolved: 'success',
        closed: 'secondary',
        rejected: 'danger',
      }
      return variants[status] || 'secondary'
    },
    getPriorityVariant(priority) {
      const variants = {
        low: 'success',
        medium: 'warning',
        high: 'danger',
        critical: 'danger',
      }
      return variants[priority] || 'secondary'
    },
  },
}
</script>
